import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const SectionContainer = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: #2d3748;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #4a5568;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const Question = styled.button<{ isOpen: boolean }>`
  width: 100%;
  text-align: left;
  padding: 1.5rem;
  background: ${props => props.isOpen ? '#f7fafc' : 'white'};
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 1.1rem;
  color: #2d3748;
  font-weight: ${props => props.isOpen ? 'bold' : 'normal'};
  transition: background-color 0.2s;

  &:hover {
    background: #f7fafc;
  }

  svg {
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.2s;
    color: #4299e1;
  }
`;

const Answer = styled.div<{ isOpen: boolean }>`
  padding: ${props => props.isOpen ? '0 1.5rem 1.5rem' : '0 1.5rem'};
  color: #4a5568;
  line-height: 1.6;
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`;

const faqs = [
  {
    question: "How many participants can join a single wheel?",
    answer: "Our plans support different participant limits. The Starter plan allows up to 50 participants, Professional up to 200, and Enterprise offers unlimited participants. Each wheel can be customized to your specific needs."
  },
  {
    question: "Can we customize the matching algorithm?",
    answer: "Yes! SpinToConnect offers various customization options for matching, including role-based matching, interest-based pairing, and random matching. Enterprise clients can also request custom matching algorithms tailored to their specific needs."
  },
  {
    question: "Is technical support included?",
    answer: "All plans include technical support. Starter plans receive email support, Professional plans include priority support, and Enterprise plans come with 24/7 dedicated support and a dedicated account manager."
  },
  {
    question: "How secure is the video calling feature?",
    answer: "We take security seriously. All video calls are end-to-end encrypted, and we comply with industry-standard security protocols. Enterprise plans include additional security features and compliance options."
  },
  {
    question: "Can SpinToConnect integrate with our existing event platform?",
    answer: "Yes, SpinToConnect offers various integration options. We provide API access in our Professional and Enterprise plans, allowing seamless integration with most popular event platforms and custom solutions."
  },
  {
    question: "What analytics and reporting features are available?",
    answer: "All plans include basic analytics such as participation rates and connection metrics. Professional and Enterprise plans offer advanced analytics including detailed engagement metrics, success rates, and custom reporting options."
  }
];

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <SectionContainer>
      <Content>
        <Header>
          <Title>Frequently Asked Questions</Title>
          <Subtitle>
            Find answers to common questions about SpinToConnect's features, 
            capabilities, and how it can benefit your organization.
          </Subtitle>
        </Header>
        <FAQList>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <Question 
                isOpen={openIndex === index}
                onClick={() => toggleQuestion(index)}
              >
                {faq.question}
                <FontAwesomeIcon icon={faChevronDown} />
              </Question>
              <Answer isOpen={openIndex === index}>
                {faq.answer}
              </Answer>
            </FAQItem>
          ))}
        </FAQList>
      </Content>
    </SectionContainer>
  );
}; 